import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BsSave, BsShieldLock } from 'react-icons/bs';
import { GridSearchInput } from '../../components/shared/GridToolbar';
import {
  LIST_CONSOLE_AG_GRID_CLASS,
  ListConsoleGridSection,
  ListConsolePage,
} from '../../components/shared/ListConsoleLayout';
import '../transaction-monitoring/TransactionMonitoring.css';
import SearchableSelect from '../../components/shared/SearchableSelect';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { DataGrid, type ColDef, type GridApi } from '../../components/ui/table';
import { cn } from '../../lib/utils';
import { showToast } from '../../store/toastStore';
import {
  fetchAcquirerOptions,
  fetchAcquirerRiskConfig,
  saveAcquirerRiskConfig,
} from './api/api';
import type { AcquirerOption, AcquirerRiskConfig, RiskRule } from './models/types';
import { createRiskRuleColumnDefs, type RiskRulesGridContext } from './table/columnDefs';
import {
  GRID_PAGE_SIZE,
  GRID_PAGE_SIZE_SELECTOR,
  RISK_RULES_GRID_DEFAULT_COL_DEF,
  riskRulesGridOptions,
} from './table/gridOptions';
import { filterRiskRules } from './utils/utils';
import '../merchants/Merchants.css';

function cloneConfig(config: AcquirerRiskConfig): AcquirerRiskConfig {
  return { ...config, rules: config.rules.map((rule) => ({ ...rule })) };
}

/** Deep-ish equality good enough for a config's editable surface. */
function sameConfig(a: AcquirerRiskConfig | null, b: AcquirerRiskConfig | null): boolean {
  if (!a || !b) return a === b;
  if (a.active !== b.active || a.reviewScore !== b.reviewScore || a.blockScore !== b.blockScore) {
    return false;
  }
  if (a.rules.length !== b.rules.length) return false;
  return a.rules.every((rule, index) => {
    const other = b.rules[index];
    return (
      other
      && rule.ruleType === other.ruleType
      && rule.enabled === other.enabled
      && rule.weight === other.weight
      && rule.threshold === other.threshold
      && rule.thresholdWindow === other.thresholdWindow
    );
  });
}

export default function RiskRules() {
  const [acquirers, setAcquirers] = useState<AcquirerOption[]>([]);
  const [acquirerId, setAcquirerId] = useState<number | null>(null);
  const [original, setOriginal] = useState<AcquirerRiskConfig | null>(null);
  const [draft, setDraft] = useState<AcquirerRiskConfig | null>(null);
  const [loadingAcquirers, setLoadingAcquirers] = useState(true);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const gridApiRef = useRef<GridApi<RiskRule> | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingAcquirers(true);
      try {
        const options = await fetchAcquirerOptions();
        if (cancelled) return;
        setAcquirers(options);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Could not load acquirers.');
      } finally {
        if (!cancelled) setLoadingAcquirers(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadConfig = useCallback(async (id: number) => {
    setLoadingConfig(true);
    setError(null);
    setOriginal(null);
    setDraft(null);
    try {
      const cfg = await fetchAcquirerRiskConfig(id);
      setOriginal(cfg);
      setDraft(cloneConfig(cfg));
    } catch (err: any) {
      setError(err?.message || 'Could not load this acquirer’s rules.');
      setOriginal(null);
      setDraft(null);
    } finally {
      setLoadingConfig(false);
    }
  }, []);

  useEffect(() => {
    if (acquirerId != null) void loadConfig(acquirerId);
    else {
      setOriginal(null);
      setDraft(null);
    }
  }, [acquirerId, loadConfig]);

  useEffect(() => {
    gridApiRef.current?.paginationGoToFirstPage();
  }, [search, acquirerId]);

  const dirty = useMemo(() => !sameConfig(original, draft), [original, draft]);

  const patchDraft = useCallback((patch: Partial<AcquirerRiskConfig>) => {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const patchRule = useCallback((ruleType: string, patch: Partial<RiskRule>) => {
    setDraft((prev) =>
      prev
        ? {
            ...prev,
            rules: prev.rules.map((rule) => (rule.ruleType === ruleType ? { ...rule, ...patch } : rule)),
          }
        : prev,
    );
  }, []);

  const handleReset = useCallback(() => {
    if (original) setDraft(cloneConfig(original));
  }, [original]);

  const handleSave = useCallback(async () => {
    if (!draft || acquirerId == null) return;
    const review = Number(draft.reviewScore);
    const block = Number(draft.blockScore);
    if (!Number.isFinite(review) || !Number.isFinite(block) || review < 1 || block < 1) {
      showToast('Review and block scores must be positive numbers.', 'error');
      return;
    }
    if (block <= review) {
      showToast('Block score should be higher than the review score.', 'error');
      return;
    }
    setSaving(true);
    try {
      const saved = await saveAcquirerRiskConfig(acquirerId, {
        active: draft.active,
        reviewScore: review,
        blockScore: block,
        rules: draft.rules.map((rule) => ({
          ruleType: rule.ruleType,
          enabled: rule.enabled,
          weight: Number.isFinite(Number(rule.weight)) ? Number(rule.weight) : rule.defaultWeight,
          // Velocity/limit rules carry an editable limit; plain conditions (NONE) have none.
          threshold: rule.thresholdKind === 'NONE' ? null
            : (rule.threshold == null ? null : Number(rule.threshold)),
          thresholdWindow: rule.thresholdKind === 'NONE' ? null
            : (rule.thresholdWindow ?? rule.defaultThresholdWindow ?? 'HOUR'),
        })),
      });
      setOriginal(saved);
      setDraft(cloneConfig(saved));
      showToast('Risk rules saved for this acquirer.', 'success');
    } catch (err: any) {
      showToast(err?.message || 'Could not save the rules.', 'error');
    } finally {
      setSaving(false);
    }
  }, [draft, acquirerId]);

  const changeAcquirer = useCallback(
    (value: string) => {
      const next = value ? Number(value) : null;
      if (next === acquirerId) return;
      if (
        dirty
        && !window.confirm('You have unsaved changes. Discard them and switch acquirer?')
      ) {
        return;
      }
      setSearch('');
      setAcquirerId(next);
    },
    [acquirerId, dirty],
  );

  const isRuleDirty = useCallback(
    (rule: RiskRule) => {
      const saved = original?.rules.find((item) => item.ruleType === rule.ruleType);
      if (!saved) return true;
      return saved.enabled !== rule.enabled || saved.weight !== rule.weight
        || saved.threshold !== rule.threshold || saved.thresholdWindow !== rule.thresholdWindow;
    },
    [original],
  );

  const filteredRules = useMemo(
    () => filterRiskRules(draft?.rules ?? [], search),
    [draft, search],
  );

  const columnDefs = useMemo<ColDef<RiskRule>[]>(() => createRiskRuleColumnDefs(), []);

  const gridContext = useMemo<RiskRulesGridContext>(
    () => ({
      isDirty: isRuleDirty,
      onWeightChange: (ruleType, weight) => patchRule(ruleType, { weight }),
      onResetWeight: (rule) => patchRule(rule.ruleType, { weight: rule.defaultWeight }),
      onEnabledChange: (ruleType, enabled) => patchRule(ruleType, { enabled }),
      onThresholdChange: (ruleType, threshold) => patchRule(ruleType, { threshold }),
      onResetThreshold: (rule) => patchRule(rule.ruleType, {
        threshold: rule.defaultThreshold,
        thresholdWindow: rule.defaultThresholdWindow,
      }),
      onWindowChange: (ruleType, thresholdWindow) => patchRule(ruleType, { thresholdWindow }),
    }),
    [isRuleDirty, patchRule],
  );

  const handleGridReady = useCallback((event: { api: GridApi<RiskRule> }) => {
    gridApiRef.current = event.api;
  }, []);

  const enabledCount = draft?.rules.filter((rule) => rule.enabled).length ?? 0;
  const enabledPct = draft && draft.rules.length > 0
    ? Math.round((enabledCount / draft.rules.length) * 100)
    : 0;

  const gridLoading = acquirerId != null && loadingConfig;
  const gridEmptyMessage = gridLoading || error
    ? ''
    : (draft?.rules.length ?? 0) === 0
      ? 'No rules returned for this acquirer.'
      : 'No rules match the current search.';

  const gridOptions = useMemo(
    () => ({
      ...riskRulesGridOptions,
      overlayNoRowsTemplate: gridEmptyMessage
        ? `<span class="text-sm text-muted-foreground">${gridEmptyMessage}</span>`
        : '<span></span>',
    }),
    [gridEmptyMessage],
  );

  const selectedAcquirer = acquirers.find((acquirer) => acquirer.id === acquirerId) ?? null;

  return (
    <ListConsolePage
      title="Rules"
      className={dirty ? 'pb-20' : undefined}
      error={error}
    >
      <ListConsoleGridSection
        toolbarLeft={
          <div className="w-72 max-w-full">
            <SearchableSelect
              className="h-9"
              value={acquirerId != null ? String(acquirerId) : ''}
              options={acquirers.map((acquirer) => ({
                value: String(acquirer.id),
                label: acquirer.name,
              }))}
              onChange={changeAcquirer}
              placeholder={loadingAcquirers ? 'Loading acquirers…' : 'Select an acquirer'}
              searchPlaceholder="Search acquirer…"
              emptyMessage={loadingAcquirers ? 'Loading acquirers…' : 'No acquirers available'}
              disabled={loadingAcquirers}
              aria-label="Acquirer"
            />
          </div>
        }
        toolbarRight={
          <div className="tm-toolbar-controls">
            <GridSearchInput
              value={search}
              onValueChange={setSearch}
              placeholder="Search rules…"
              className="tm-toolbar-search"
              disabled={acquirerId == null || gridLoading || (draft?.rules.length ?? 0) === 0}
              aria-label="Search rules"
            />
          </div>
        }
        between={
          draft ? (
        <div
          className={cn(
            'flex shrink-0 flex-wrap items-center justify-between gap-4 rounded-[10px] border px-4 py-3',
            draft.active
              ? 'border-border bg-muted/40'
              : 'border-amber-500/35 bg-amber-500/10',
          )}
        >
          <div className="flex items-center gap-3">
            <Switch
              checked={draft.active}
              onCheckedChange={(value) => patchDraft({ active: value === true })}
              id="rr-active"
              aria-label="Rule set active"
            />
            <label htmlFor="rr-active" className="flex cursor-pointer flex-col">
              <span className="text-sm font-medium text-foreground">Rule set active</span>
              <span className="text-xs text-muted-foreground">
                {!draft.active
                  ? 'Inactive — none of these rules are applied, even on MID-protected terminals.'
                  : draft.configured
                    ? `Saved for ${selectedAcquirer?.name ?? 'this acquirer'}.`
                    : 'Not saved yet — showing recommended defaults.'}
              </span>
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-5">
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              Block at score
              <Input
                type="number"
                min={1}
                max={100}
                className="h-9 w-20"
                value={draft.blockScore}
                onChange={(event) => patchDraft({ blockScore: Number(event.target.value) })}
                aria-label="Block at score"
              />
            </label>
            {draft.rules.length > 0 ? (
              <div className="flex flex-col items-end gap-1">
                <span className="text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">{enabledCount}</span> of{' '}
                  {draft.rules.length} on
                </span>
                <div className="h-1 w-28 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary"
                    style={{ width: `${enabledPct}%` }}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>
          ) : null
        }
      >
        {acquirerId == null ? (
          <div className="flex min-h-[280px] flex-col items-center justify-center gap-2 p-8 text-center text-sm text-muted-foreground">
            <BsShieldLock className="mb-1 h-7 w-7 text-muted-foreground" />
            <h3 className="text-base font-medium text-foreground">Pick an acquirer to set its rules</h3>
            <p className="max-w-md text-sm leading-relaxed">
              Rules are configured per acquirer and applied acquirer-wide. Choose one above to see
              its current rule set — every acquirer starts from the recommended defaults until you
              save.
            </p>
          </div>
        ) : (
          <DataGrid<RiskRule>
            rowData={filteredRules}
            columnDefs={columnDefs}
            context={gridContext}
            defaultColDef={RISK_RULES_GRID_DEFAULT_COL_DEF}
            gridOptions={gridOptions}
            includeAuditColumns={false}
            loading={gridLoading}
            pagination
            paginationPageSize={GRID_PAGE_SIZE}
            paginationPageSizeSelector={GRID_PAGE_SIZE_SELECTOR}
            height="100%"
            className={LIST_CONSOLE_AG_GRID_CLASS}
            onGridReady={handleGridReady}
          />
        )}
      </ListConsoleGridSection>

      {dirty && draft ? (
        <div className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-card px-4 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.10)]">
          <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-3">
            <span className="text-sm">
              <span className="font-medium text-[#b7791f]">Unsaved changes</span>
              <span className="text-muted-foreground">
                {' '}
                — save or discard before switching acquirers
              </span>
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleReset} disabled={saving}>
                Reset
              </Button>
              <Button onClick={handleSave} disabled={saving} className="gap-1.5">
                <BsSave className="h-3.5 w-3.5" />
                {saving ? 'Saving…' : 'Save rules'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </ListConsolePage>
  );
}
